import React, { useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useLang } from "@/contexts/LangContext";

/**
 * FavoriteButton — premium wishlist toggle. Optimistic, accessible, swallows
 * its own click so it can be safely nested inside a product card <Link>.
 *
 * Variants:
 *   "card"   : tiny overlay button used on ProductCard top-right
 *   "detail" : full bordered button used on ProductDetail
 */
export default function FavoriteButton({ productId, variant = "card", productName }) {
  const { isFavorite, toggle } = useFavorites();
  const { lang } = useLang();
  const [bursting, setBursting] = useState(false);
  const active = isFavorite(productId);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const wasActive = active;
    await toggle(productId);
    if (!wasActive) {
      setBursting(true);
      setTimeout(() => setBursting(false), 400);
    }
    const msg = wasActive
      ? (lang === "tr" ? "Favorilerden çıkarıldı" : "Removed from favorites")
      : (lang === "tr" ? "Favorilere eklendi" : "Added to favorites");
    toast.success(msg, { duration: 1500 });
  };

  const label = active
    ? (lang === "tr" ? "Favorilerden çıkar" : "Remove from favorites")
    : (lang === "tr" ? "Favorilere ekle" : "Add to favorites");

  if (variant === "detail") {
    return (
      <button
        type="button"
        data-testid={`fav-btn-detail-${productId}`}
        aria-label={label}
        aria-pressed={active}
        onClick={onClick}
        className={"relative border p-4 transition-colors " + (active
          ? "border-[#C8A96A] bg-[#C8A96A] text-white"
          : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white")}
      >
        <Heart size={16} fill={active ? "currentColor" : "none"} className={bursting ? "fav-burst" : ""} />
        <style>{`@keyframes fav-burst-kf { 0% { transform: scale(1); } 40% { transform: scale(1.35); } 100% { transform: scale(1); } } .fav-burst { animation: fav-burst-kf 380ms ease-out; }`}</style>
      </button>
    );
  }

  // "card" variant
  return (
    <button
      type="button"
      data-testid={`fav-btn-${productId}`}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={"absolute top-3 right-3 z-10 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur-md transition-all duration-200 " + (active
        ? "bg-[#C8A96A] text-white shadow-md"
        : "bg-white/85 text-[#0A0A0A] hover:bg-white shadow-sm hover:scale-105 opacity-90 group-hover:opacity-100")}
    >
      <Heart size={16} fill={active ? "currentColor" : "none"} strokeWidth={1.8} className={bursting ? "fav-burst" : ""} />
      <span className="sr-only">{productName || label}</span>
      <style>{`@keyframes fav-burst-kf { 0% { transform: scale(1); } 40% { transform: scale(1.35); } 100% { transform: scale(1); } } .fav-burst { animation: fav-burst-kf 380ms ease-out; }`}</style>
    </button>
  );
}
